import React, { useEffect, useMemo, useState } from "react";
import { flattenLocaleObject, restoreLocaleObject } from "../localeUtils";
import AdminPanel from "../shared/AdminPanel";
import panelStyles from "../styles/Panel.module.css";
import styles from "../styles/LocalesSection.module.css";

function normalizeForSearch(value) {
  return String(value ?? "").toLowerCase();
}

function buildLocaleGroups(entries) {
  const groups = [];
  const groupsMap = new Map();

  entries.forEach((entry) => {
    if (!groupsMap.has(entry.group)) {
      const nextGroup = { name: entry.group, entries: [], changedCount: 0 };
      groupsMap.set(entry.group, nextGroup);
      groups.push(nextGroup);
    }

    const group = groupsMap.get(entry.group);
    group.entries.push(entry);
    if (entry.changed) {
      group.changedCount += 1;
    }
  });

  return groups;
}

function formatKeysCount(count) {
  if (count === 1) return "1 ключ";
  return `${count} ключей`;
}

function LocalesSectionBody({
  adminToken,
  localeLang,
  localesList,
  localeData,
  localeInitialData,
  localeSaving,
  localeDirty,
  onLocaleLangChange,
  onLocaleChange,
  onReload,
  onSave
}) {
  const [localeFilter, setLocaleFilter] = useState("");
  const [showChangedOnly, setShowChangedOnly] = useState(false);

  useEffect(() => {
    setLocaleFilter("");
    setShowChangedOnly(false);
  }, [localeLang]);

  const currentEntries = useMemo(() => flattenLocaleObject(localeData || {}), [localeData]);
  const initialEntries = useMemo(() => flattenLocaleObject(localeInitialData || {}), [localeInitialData]);

  const decoratedEntries = useMemo(() => {
    const initialEntriesMap = new Map(initialEntries.map((entry) => [entry.path, entry]));

    return currentEntries.map((entry) => {
      const initialEntry = initialEntriesMap.get(entry.path);
      const changed =
        !initialEntry ||
        initialEntry.value !== entry.value ||
        initialEntry.valueType !== entry.valueType;

      return {
        ...entry,
        changed
      };
    });
  }, [currentEntries, initialEntries]);

  const filteredEntries = useMemo(() => {
    const needle = normalizeForSearch(localeFilter).trim();

    return decoratedEntries.filter((entry) => {
      if (showChangedOnly && !entry.changed) return false;
      if (!needle) return true;

      const matchesPath = normalizeForSearch(entry.path).includes(needle);
      const matchesValue = normalizeForSearch(entry.value).includes(needle);
      const matchesPlaceholder = entry.placeholders.some((token) => normalizeForSearch(token).includes(needle));

      return matchesPath || matchesValue || matchesPlaceholder;
    });
  }, [decoratedEntries, localeFilter, showChangedOnly]);

  const groups = useMemo(() => buildLocaleGroups(filteredEntries), [filteredEntries]);
  const changedCount = useMemo(
    () => decoratedEntries.filter((entry) => entry.changed).length,
    [decoratedEntries]
  );

  const handleLocaleValueChange = (path, nextValue) => {
    const nextEntries = currentEntries.map((entry) => {
      if (entry.path !== path) return entry;
      return {
        ...entry,
        value: nextValue
      };
    });

    try {
      onLocaleChange(restoreLocaleObject(nextEntries));
    } catch (error) {
      window.alert(error.message || "Не удалось обновить значение локали.");
    }
  };

  return (
    <>
      <div className={styles.help}>
        Не меняйте плейсхолдеры вроде <code>{"{{name}}"}</code> или <code>{"{{count}}"}</code>, если специально не редактируете шаблон.
      </div>

      <div className={styles.toolbar}>
        <div className={styles.controls}>
          <select
            value={localeLang}
            onChange={(event) => onLocaleLangChange(event.target.value)}
            disabled={!adminToken}
            className={panelStyles.select}
          >
            {localesList.length > 0 ? (
              localesList.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))
            ) : (
              <option value="ru">ru</option>
            )}
          </select>

          <input
            type="text"
            placeholder="Поиск по namespace, ключу, тексту или плейсхолдеру"
            value={localeFilter}
            onChange={(event) => setLocaleFilter(event.target.value)}
            className={panelStyles.input}
          />

          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={showChangedOnly}
              onChange={(event) => setShowChangedOnly(event.target.checked)}
            />
            <span>Только измененные</span>
          </label>
        </div>

        <div className={styles.summary}>
          <span>
            {filteredEntries.length} of {decoratedEntries.length}
          </span>
          <span>Изменено: {changedCount}</span>
          <span>Групп: {groups.length}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        <button
          type="button"
          className={panelStyles.buttonSecondary}
          onClick={onReload}
          disabled={!adminToken || localeSaving}
        >
          Перезагрузить
        </button>
        <button
          type="button"
          className={panelStyles.button}
          onClick={onSave}
          disabled={!localeDirty || localeSaving}
        >
          {localeSaving ? "Сохраняем..." : "Сохранить"}
        </button>
      </div>

      {groups.length > 0 ? (
        <div className={styles.groups}>
          {groups.map((group) => (
            <div className={styles.group} key={group.name}>
              <div className={styles.groupHeader}>
                <div>
                  <div className={styles.groupTitle}>{group.name}</div>
                  <div className={styles.groupMeta}>{formatKeysCount(group.entries.length)}</div>
                </div>
                {group.changedCount > 0 ? (
                  <div className={styles.groupChanged}>Изменено: {group.changedCount}</div>
                ) : null}
              </div>

              <div className={styles.list}>
                {group.entries.map((entry) => (
                  <div className={styles.item} key={entry.path}>
                    <div className={styles.head}>
                      <div className={styles.key}>{entry.path}</div>
                      {entry.changed ? <span className={styles.status}>Изменено</span> : null}
                    </div>

                    {entry.placeholders.length > 0 ? (
                      <div className={styles.placeholders}>
                        {entry.placeholders.map((token) => (
                          <span className={styles.badge} key={`${entry.path}-${token}`}>
                            {token}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    <textarea
                      rows={entry.multiline ? 6 : 3}
                      value={entry.value ?? ""}
                      onChange={(event) => handleLocaleValueChange(entry.path, event.target.value)}
                      className={panelStyles.textarea}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>По текущим фильтрам ничего не найдено.</div>
      )}
    </>
  );
}

export default function LocalesSection(props) {
  const { mode = "panel", isOpen = true, onToggle, ...rest } = props;

  if (mode === "page") {
    return <LocalesSectionBody {...rest} />;
  }

  return (
    <AdminPanel id="locales" title="Локали" isOpen={isOpen} onToggle={onToggle}>
      <LocalesSectionBody {...rest} />
    </AdminPanel>
  );
}
